import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import { OrderModel } from "../Models/OrderModel";

export type OrderPatch = Partial<OrderModel> | ((prev: OrderModel) => Partial<OrderModel>);

export type OrderAction =
	| { type: "update"; patch: OrderPatch }
	| { type: "reset" };

export const orderReducer = (state: OrderModel, action: OrderAction): OrderModel => {
	switch (action.type) {
		case "update": {
			const patch = typeof action.patch === "function" ? action.patch(state) : action.patch;
			return { ...state, ...patch };
		}
		case "reset":
			return new OrderModel();
		default:
			return state;
	}
};

export interface IOrderContext {
	order: OrderModel;
	/** Returns the latest order, including updates made earlier in the same event/callback that haven't rendered yet. */
	getOrder: () => OrderModel;
	/** Merges the patch into the latest order state; safe to call from async code and effects. */
	updateOrder: (patch: OrderPatch) => void;
	resetOrder: () => void;
}

const OrderContext = createContext<IOrderContext | undefined>(undefined);

interface IOrderProvider {
	initialOrder?: OrderModel;
	children: React.ReactNode;
}

export const OrderProvider = ({ initialOrder, children }: IOrderProvider) => {
	const latestOrder = useRef<OrderModel>(initialOrder ?? new OrderModel());
	const [order, setOrderState] = useState<OrderModel>(latestOrder.current);

	// Reduced eagerly into a ref so getOrder() is correct before React re-renders.
	const dispatch = useCallback((action: OrderAction) => {
		latestOrder.current = orderReducer(latestOrder.current, action);
		setOrderState(latestOrder.current);
	}, []);

	const getOrder = useCallback(() => latestOrder.current, []);
	const updateOrder = useCallback((patch: OrderPatch) => dispatch({ type: "update", patch }), [dispatch]);
	const resetOrder = useCallback(() => dispatch({ type: "reset" }), [dispatch]);

	const value = useMemo(
		() => ({ order, getOrder, updateOrder, resetOrder }),
		[order, getOrder, updateOrder, resetOrder]
	);

	return <OrderContext.Provider value={value}>{children}</OrderContext.Provider>;
};

export const useOrder = (): IOrderContext => {
	const context = useContext(OrderContext);
	if (!context) {
		throw new Error("useOrder must be used within an OrderProvider");
	}
	return context;
};

export default OrderContext;
